<template>
	<form ref="form" class="page" @submit.prevent="submit()">
		<template v-if="tickets && tickets.length">
			<h1 class="page__title">{{ $t('letbane.buy_ticket') }}</h1>

			<div v-if="!isMobilePayPayment" class="page__toggle toggle">
				<label for="letbanetoggle" class="toggle__label" :class="{ 'toggle__label--checked': !isEmailToggled }">
					{{ $t('letbane.sms_uppercase') }}
				</label>
				<label class="toggle__input">
					<input id="letbanetoggle" v-model="isEmailToggled" type="checkbox" class="toggle__input--switch" />
					<span class="toggle__input--slider"></span>
				</label>
				<label for="letbanetoggle" class="toggle__label" :class="{ 'toggle__label--checked': isEmailToggled }">
					{{ $t('letbane.email_uppercase') }}
				</label>
			</div>

			<template v-if="isMobilePayPayment">
				<label for="letbanephoneinput" class="page__label">{{ $t('letbane.phone_label') }}</label>
				<div class="page__countrycode-phone">
					<vue-country-code
						ref="vcc"
						:class="{ 'vue-country-select--error': phoneError }"
						default-country="DK"
						:only-countries="['DK', 'FI']"
						:enabled-country-code="true"
						@onSelect="onCountryCodeSelect"
					>
					</vue-country-code>
					<div class="page__countrycode-separator" :class="{ 'page__countrycode-separator--error': phoneError }">
						<div class="page__countrycode-separator--line"></div>
					</div>
					<input
						id="letbanephoneinput"
						v-model="phoneInput"
						type="tel"
						autocomplete="tel"
						class="page__phone-input"
						:class="{ 'page__phone-input--error': phoneError }"
					/>
				</div>
			</template>
			<template v-if="!isMobilePayPayment && isEmailToggled">
				<label for="letbaneemailinput" class="page__label">{{ $t('letbane.email_label') }}</label>
				<input
					id="letbaneemailinput"
					v-model="emailInput"
					type="email"
					class="page__email-input"
					:class="{ 'page__email-input--error': emailError }"
					:placeholder="$t('letbane.email_placeholder')"
				/>
			</template>
			<template v-if="!isMobilePayPayment && !isEmailToggled">
				<label for="letbanephoneinputcountrycode" class="page__label">{{ $t('letbane.phone_label') }}</label>
				<div class="page__countrycode-phone">
					<vue-country-code
						ref="vcc"
						default-country="DK"
						:preferred-countries="['NO', 'FR', 'IT', 'GB', 'DE', 'ES', 'SE', 'NL', 'US', 'CZ']"
						:class="{ 'vue-country-select--error': phoneError }"
						:enabled-country-code="true"
						@onSelect="onCountryCodeSelect"
					></vue-country-code>
					<div class="page__countrycode-separator" :class="{ 'page__countrycode-separator--error': phoneError }">
						<div class="page__countrycode-separator--line"></div>
					</div>
					<input
						id="letbanephoneinputcountrycode"
						v-model="phoneInput"
						type="tel"
						autocomplete="tel"
						class="page__phone-input"
						:class="{ 'page__phone-input--error': phoneError }"
					/>
				</div>
			</template>

			<label for="tickets" class="page__label">{{ $t('letbane.choose_tickets') }}</label>
			<div class="page__tickets" :class="{ 'page__tickets--error': ticketAmountError }" data-id="tickets">
				<div v-for="(ticket, index) in tickets" :key="ticket.id" class="ticket">
					<div class="ticket__text">
						<span class="ticket__name">{{ ticket.texts[selectedLanguage].name }}</span>
						<button ref="info" class="ticket__info-button reset-button" type="button" @click="onInfoButton(index)">
							<span>i</span>
						</button>
					</div>

					<span class="ticket__price">{{ ticket.price | prettyPrice }}</span>

					<div class="ticket__amount-container">
						<button class="ticket__decrease reset-button" type="button" @click="onDecreaseButton(ticket)">
							<div class="ticket__line--horizontal"></div>
						</button>
						<span class="ticket__current-amount">{{ ticket.currentAmount }}</span>
						<button class="ticket__increase reset-button" type="button" @click="onIncreaseButton(ticket)">
							<div class="ticket__line--horizontal"></div>
							<div class="ticket__line--vertical"></div>
						</button>
					</div>
				</div>
				<div class="warning">
					{{ tickets[0].texts[selectedLanguage].validityText }}
				</div>
			</div>

			<div class="page__checkboxes">
				<div class="checkbox">
					<input
						id="letbanecheckboxtac"
						v-model="acceptedTermsAndConditions"
						class="checkbox__input"
						:class="{ 'checkbox__input--error': termsAndConditionsError }"
						type="checkbox"
					/>
					<label for="letbanecheckboxtac" class="checkbox__label">
						{{ $t('letbane.i_accept') }}&nbsp;
						<button class="checkbox__button reset-button" type="button" @click="$modal.show('letbane-terms-modal')">
							<span>{{ $t('letbane.terms_and_conditions') }}</span>
							<div class="checkbox__underline"></div>
						</button>
					</label>
				</div>
			</div>

			<button class="page__paybutton reset-button" :class="{ 'page__paybutton--valid': isValid }" type="submit">
				<template v-if="isMobilePayPayment">
					<img src="@/apps/letbane/assets/images/mobilepay-logo-white.svg" />
					<span>{{ $t('letbane.pay_with_mobilepay') }}</span>
				</template>
				<template v-else>
					<img src="@/apps/letbane/assets/images/cardpay-logo-white.svg" />
					<span>{{ $t('letbane.buy_ticket') }}</span>
				</template>

				<span v-if="ticketsTotalPrice > 0">&nbsp;{{ ticketsTotalPrice | prettyPrice }}</span>
			</button>

			<button class="page__payment-options options reset-button" type="button" @click="changePaymentMethod()">
				<div>
					<span class="options__text">{{ $t('letbane.other_payment_options') }}</span>
					<div class="options__underline"></div>
				</div>
			</button>

			<div class="logos">
				<div v-if="isMobilePayPayment" class="logos__cards">
					<img src="@/apps/letbane/assets/images/visa-logo.svg" class="logos__card" />
					<img src="@/apps/letbane/assets/images/mastercard-logo.svg" class="logos__card" />
					<img src="@/apps/letbane/assets/images/apple-pay-logo.svg" class="logos__card logos__card--apple" />
					<img src="@/apps/letbane/assets/images/paypal-logo.jpg" class="logos__card logos__card--paypal" />
				</div>
				<img v-else src="@/apps/letbane/assets/images/mobilepay-logo.svg" class="logos__mobilepay" />
			</div>

			<modal-letbane-terms :name="termsAndConditionsLetbane" />
			<modal-letbane-tooltip :title="tooltipTitle" :text="tooltipText" />
		</template>
	</form>
</template>

<script>
import { mapGetters } from 'vuex'
import { callApi } from '@/api/api-core'
import { i18nInstance } from '@/i18n'
import VueCountryCode from 'vue-country-code'
import { initializeMobilePay, initializeDibsEasy } from '@/api/modules/confirm'
import globals from '../globals.js'

const ModalLetbaneTerms = () =>
	import(/* webpackMode: "lazy", webpackChunkName: "modal-letbane-terms" */ '@/components/modals/ModalLetbaneTerms')
const ModalLetbaneTooltip = () =>
	import(/* webpackMode: "lazy", webpackChunkName: "modal-letbane-tooltip" */ '@/components/modals/ModalLetbaneTooltip')

const defaultForm = {
	acceptedTermsAndConditions: false,
	summedTicketAmount: 0,
	email: null,
	phone: null,
	isEmailToggled: false
}

export default {
	components: {
		ModalLetbaneTerms,
		ModalLetbaneTooltip
	},
	data() {
		return {
			hasSubmit: false,
			tooltipTitle: '',
			tooltipText: '',
			isCustomerConfirmationOpen: false,
			isMobilePayPayment: true,
			tickets: null,
			maximumNumberOfTickets: 0,
			selectedCountryCodeObject: null,
			orderId: null,
			phoneError: false,
			emailError: false,
			ticketAmountError: false,
			termsAndConditionsError: false,
			isValid: false,
			form: { ...defaultForm }
		}
	},
	computed: {
		...mapGetters({
			getSecondaryWebshop: 'system/getSecondaryWebshop',
			passengerTypes: 'passenger/getPassengerTypes'
		}),
		selectedLanguage() {
			return i18nInstance().locale
		},
		adultPassengerType() {
			return this.passengerTypes.filter(x => x.stringIdentifier === 'Adult')[0]
		},
		ticketsTotalPrice() {
			let totalPrice = 0

			if (this.tickets) {
				this.tickets.forEach(ticket => {
					if (ticket.currentAmount > 0) {
						totalPrice = totalPrice + ticket.currentAmount * ticket.price
					}
				})
			}

			return totalPrice
		},
		termsAndConditionsLetbane: function() {
			switch (this.$i18n.locale) {
				case 'da':
				case 'en':
					return 'TermsAndConditions'
				default:
					throw new Error('Unsupported language')
			}
		},
		returnUrl() {
			return window.location.origin + '/paymentcomplete'
		},
		odenseTramIdentifier() {
			return this.getSecondaryWebshop.filter(webshop => webshop.identifier === 'OdenseTram')[0].identifier
		},
		nemBilletIdentifier() {
			if (this.$route.query.salesChannel) {
				const query = this.$route.query.salesChannel
				return query.charAt(0).toUpperCase() + query.slice(1)
			}

			return null
		},
		odenseTramShowCustomerConfirmation() {
			return this.getSecondaryWebshop.filter(webshop => webshop.identifier === 'OdenseTram')[0].showCustomerConfirmation
		},
		salesChannelIdentifier() {
			return 'SecondaryWebshop'
		},
		isEmailToggled: {
			get: function() {
				return this.form.isEmailToggled
			},
			set: function(newValue) {
				this.form.isEmailToggled = newValue
				if (this.form.isEmailToggled) {
					this.selectedCountryCodeObject = null
				}
				this.resetInputs()
				this.checkValidity()
			}
		},
		emailInput: {
			get: function() {
				return this.form.email
			},
			set: function(newValue) {
				if (newValue !== '') {
					this.form.email = newValue
				} else {
					this.form.email = null
				}

				this.emailError = false
				this.checkValidity()
			}
		},
		phoneInput: {
			get: function() {
				return this.form.phone
			},
			set: function(newValue) {
				if (newValue) {
					this.form.phone = newValue.replace(/\s/g, '')

					if (this.selectedCountryCodeObject && this.form.phone.includes('+')) {
						this.form.phone = this.form.phone.replace(`+${this.selectedCountryCodeObject.dialCode}`, '')
					}

					this.validatePhoneNumber(this.form.phone)
				} else {
					this.form.phone = null
				}

				this.phoneError = false
				this.checkValidity()
			}
		},
		summedTicketAmount: {
			get: function() {
				return this.form.summedTicketAmount
			},
			set: function(newValue) {
				this.form.summedTicketAmount = newValue
				this.ticketAmountError = false
				this.checkValidity()
			}
		},
		acceptedTermsAndConditions: {
			get: function() {
				return this.form.acceptedTermsAndConditions
			},
			set: function(newValue) {
				this.form.acceptedTermsAndConditions = newValue
				this.termsAndConditionsError = false
				this.checkValidity()
			}
		}
	},
	watch: {
		'$i18n.locale'(newValue) {
			if (newValue === 'da' && this.isMobilePayPayment) {
				this.selectedCountryCodeObject = this.selectedCountryCodeObject = VueCountryCode.methods.findCountry('DK')
			}

			if (newValue === 'en' && this.isMobilePayPayment) {
				this.changePaymentMethod()
			}
		}
	},
	mounted() {
		this.selectedCountryCodeObject = VueCountryCode.methods.findCountry('DK')
		this.getFixedPriceTickets()

		if (this.selectedLanguage === 'en' && this.isMobilePayPayment) {
			this.changePaymentMethod(false)
		}

		window.history.replaceState({ isMobilePayPayment: this.isMobilePayPayment }, '', '')
		window.addEventListener('popstate', event => {
			if (event.state) {
				this.isMobilePayPayment = event.state.isMobilePayPayment
			}
		})
	},
	methods: {
		refreshPage() {
			window.location.reload(true)
		},
		routeToError(query) {
			this.$router.push({ path: '/error', query: { error: query } })
		},
		onCountryCodeSelect(countrycodeObject) {
			this.selectedCountryCodeObject = countrycodeObject
			this.checkValidity()
		},
		onIncreaseButton(ticket) {
			if (ticket.currentAmount < this.maximumNumberOfTickets) {
				ticket.currentAmount = ticket.currentAmount + 1
				this.summedTicketAmount = this.tickets.map(x => x.currentAmount).reduce((partialSum, a) => partialSum + a, 0)
			}
		},
		onDecreaseButton(ticket) {
			if (ticket.currentAmount > 0) {
				ticket.currentAmount = ticket.currentAmount - 1
				this.summedTicketAmount = this.tickets.map(x => x.currentAmount).reduce((partialSum, a) => partialSum + a, 0)
			}
		},
		onInfoButton(index) {
			this.tooltipTitle = `${this.$t('letbane.info_about_ticket')} – ${this.tickets[index].texts[this.selectedLanguage].name}`
			this.tooltipText = this.tickets[index].texts[this.selectedLanguage].description
			this.$modal.show('letbane-tooltip-modal')
		},
		changePaymentMethod(setHistory = true) {
			this.isMobilePayPayment = !this.isMobilePayPayment
			if (this.isMobilePayPayment) {
				this.selectedCountryCodeObject = VueCountryCode.methods.findCountry('DK')
			} else if (!this.isMobilePayPayPayment && this.isEmailToggled) {
				this.selectedCountryCodeObject = null
			}
			this.resetInputs()
			this.$nextTick(() => {
				if (this.$refs.vcc) {
					this.$refs.vcc.choose(this.$refs.vcc.findCountry('DK')) // when changing payment method, default back to DK
				}
			})

			if (setHistory) {
				window.history.pushState({ isMobilePayPayment: this.isMobilePayPayment }, '', '')
			}
		},
		resetInputs() {
			this.phoneError = false
			this.emailError = false
			this.ticketAmountError = false
			this.termsAndConditionsError = false
		},
		validatePhoneNumber(number) {
			const dataForSelectedCountry = globals.phoneData.filter(item => item.code === this.selectedCountryCodeObject.iso2)

			if (number && dataForSelectedCountry) {
				return (
					dataForSelectedCountry[0].phoneLength === number.length ||
					(number.length >= dataForSelectedCountry[0].min && number.length <= dataForSelectedCountry[0].max) ||
					(Array.isArray(dataForSelectedCountry[0].phoneLength) && dataForSelectedCountry[0].phoneLength.includes(number.length))
				)
			}

			return false
		},
		checkValidity() {
			if (
				(!this.isMobilePayPayment && this.isEmailToggled && !this.emailInput) ||
				(!this.isMobilePayPayment && this.isEmailToggled && !this.emailInput.match(/^\S+@\S+\.\S+$/))
			) {
				this.isValid = false
			} else if (
				(this.isMobilePayPayment && !this.phoneInput) ||
				(this.isMobilePayPayment && this.phoneInput && !this.validatePhoneNumber(this.phoneInput)) ||
				(!this.isMobilePayPayment && !this.isEmailToggled && !this.phoneInput) ||
				(!this.isMobilePayPayment && !this.isEmailToggled && this.phoneInput && !this.validatePhoneNumber(this.phoneInput))
			) {
				this.isValid = false
			} else if (this.summedTicketAmount <= 0) {
				this.isValid = false
			} else if (!this.acceptedTermsAndConditions) {
				this.isValid = false
			} else {
				this.isValid = true
			}
		},
		openCustomerConfirmation() {
			this.isCustomerConfirmationOpen = true
		},
		closeCustomerConfirmation() {
			this.isCustomerConfirmationOpen = false
		},
		submit(confirmed = false) {
			if (
				(!this.isMobilePayPayment && this.isEmailToggled && !this.emailInput) ||
				(!this.isMobilePayPayment && this.isEmailToggled && !this.emailInput.match(/^\S+@\S+\.\S+$/))
			) {
				this.emailError = true
			}

			if (
				(this.isMobilePayPayment && !this.phoneInput) ||
				(this.isMobilePayPayment && this.phoneInput && !this.validatePhoneNumber(this.phoneInput)) ||
				(!this.isMobilePayPayment && !this.isEmailToggled && !this.phoneInput) ||
				(!this.isMobilePayPayment && !this.isEmailToggled && this.phoneInput && !this.validatePhoneNumber(this.phoneInput))
			) {
				this.phoneError = true
			}

			if (this.summedTicketAmount <= 0) {
				this.ticketAmountError = true
			}

			if (!this.acceptedTermsAndConditions) {
				this.termsAndConditionsError = true
			}

			if (!this.isValid) {
				return
			}

			if (this.odenseTramShowCustomerConfirmation && !confirmed) {
				this.openCustomerConfirmation()
				return
			}

			if (!this.isSubmit) {
				this.isSubmit = true
				this.order().then(() => {
					this.claim().then(() => {
						this.delivery().then(() => {
							if (this.isMobilePayPayment) {
								initializeMobilePay(this.orderId).then(response => {
									if (!response.ok) {
										throw new Error(JSON.stringify(response))
									}
									this.isSubmit = false
									window.location.href = response.responseBody.redirectUrl
								})
							} else {
								initializeDibsEasy(this.orderId, this.returnUrl).then(response => {
									if (!response.ok) {
										throw new Error(JSON.stringify(response))
									}
									this.$cookies.set(`letbane-order-${response.responseBody.paymentId}`, this.orderId, '30min')
									this.isSubmit = false

									let language = 'da-DK'
									if (this.selectedLanguage === 'en') {
										language = 'en-GB'
									}

									window.location.href = response.responseBody.hostedPaymentPageUrl + `&language=${language}`
								})
							}
						})
					})
				})
			}
		},
		getFixedPriceTickets() {
			const requestHeaders = {
				'X-MobileTicket-ClientCompany': this.nemBilletIdentifier || this.odenseTramIdentifier,
				'X-MobileTicket-ClientLanguage': this.selectedLanguage
			}

			const requestBody = {
				salesChannelIdentifier: this.salesChannelIdentifier
			}

			return callApi('POST', 'v12/products/fixedpriceticket/info', requestBody, requestHeaders)
				.then(response => {
					if (
						response.responseBody.buyableProductVariants &&
						response.responseBody.buyableProductVariants.length &&
						response.responseBody.allowedPassengerTypeIds.includes(this.adultPassengerType.id)
					) {
						this.maximumNumberOfTickets = response.responseBody.maximumNumberOfPassengersPerItem
						this.tickets = response.responseBody.buyableProductVariants
						this.tickets.forEach(ticket => {
							// using $set to make the property in the object reactive, so that vue can re-render when property changes
							this.$set(ticket, 'currentAmount', 0)

							this.$set(
								ticket,
								'price',
								ticket.passengerTypePrices.filter(price => price.passengerTypeId === this.adultPassengerType.id)[0]
									.passengerTypePriceOre
							)
						})
					} else {
						this.routeToError()
					}
				})
				.catch(() => {
					this.routeToError()
				})
		},
		order() {
			const requestHeaders = {
				'X-MobileTicket-ClientCompany': this.nemBilletIdentifier || this.odenseTramIdentifier,
				'X-MobileTicket-ClientLanguage': this.selectedLanguage
			}

			const requestBody = {
				salesChannelIdentifier: this.salesChannelIdentifier,
				validFromInstant: null,
				products: []
			}

			this.tickets.forEach(ticket => {
				if (ticket.currentAmount > 0) {
					requestBody.products.push({
						productVariantId: ticket.id,
						passengers: [
							{
								passengerTypeId: this.adultPassengerType.id,
								passengerTypeCount: ticket.currentAmount
							}
						]
					})
				}
			})

			return callApi('POST', 'v5/products/fixedpriceticket/order', requestBody, requestHeaders).then(response => {
				this.orderId = response.responseBody.orderId
			})
		},
		claim() {
			const requestHeaders = {
				'X-MobileTicket-ClientCompany': this.nemBilletIdentifier || this.odenseTramIdentifier,
				'X-MobileTicket-ClientLanguage': this.selectedLanguage
			}

			const requestBody = {
				orderId: this.orderId,
				customerName: null,
				email: this.isEmailToggled ? this.emailInput : null,
				CountryCode: this.selectedCountryCodeObject && !this.isEmailToggled ? this.selectedCountryCodeObject.dialCode : null,
				phoneNumber: !this.isEmailToggled ? this.phoneInput : null
			}

			return callApi('POST', 'v1/basket/claim/accountless', requestBody, requestHeaders)
		},
		delivery() {
			const requestHeaders = {
				'X-MobileTicket-ClientCompany': this.nemBilletIdentifier || this.odenseTramIdentifier,
				'X-MobileTicket-ClientLanguage': this.selectedLanguage
			}

			const requestBody = {
				orderId: this.orderId
			}

			return callApi('POST', 'v2/delivery/webshop/setdelivery', requestBody, requestHeaders)
		}
	}
}
</script>

<style lang="scss" scoped>
.page {
	flex: 1;
	display: flex;
	flex-direction: column;
	margin-top: 0.625rem;

	&__title {
		font-size: 1.625rem;
		text-align: center;
		margin-top: 0;
		margin-bottom: 0.75rem;
	}

	&__toggle {
		display: grid;
		align-items: center;
		grid-template-columns: 1fr 3.875rem 1fr;
		column-gap: 0.75rem;
		margin-bottom: 0.75rem;
	}

	&__label {
		display: inline-block;
		margin-bottom: 0.625rem;
		font-size: clamp(0.75rem, 3.5vw, 0.875rem);
	}

	&__countrycode-phone {
		display: flex;
		align-items: center;
		margin-bottom: 0.6875rem;
		height: 2.75rem;

		.page__phone-input {
			border-top-left-radius: 0 !important;
			border-bottom-left-radius: 0 !important;
			border-left: none !important;
			margin-bottom: 0 !important;
		}
	}

	&__countrycode-separator {
		width: 0.0625rem;
		height: 100%;
		background-color: $letbane-color-white;
		border-top: 0.0625rem solid $letbane-color-stroke;
		border-bottom: 0.0625rem solid $letbane-color-stroke;
		display: flex;
		align-items: center;
		box-sizing: border-box;

		&--error {
			border-top: 0.0625rem solid red;
			border-bottom: 0.0625rem solid reD;
		}

		&--line {
			height: 1.625rem;
			width: 0.0625rem;
			background-color: $letbane-color-stroke;
		}
	}

	&__tickets {
		margin-bottom: 0.625rem;
		background-color: $letbane-color-white;
		border: 0.0625rem solid $letbane-color-stroke;
		border-radius: 0.25rem;

		&--error {
			border: 0.0625rem solid red;
		}
	}

	&__paybutton {
		display: flex;
		justify-content: center;
		align-items: center;
		font-size: 16px;
		width: 100%;
		height: 47px;
		background-color: $letbane-color-999;
		color: $letbane-color-white;
		border-radius: 0.375rem;
		margin-bottom: 0.9375rem;
		cursor: pointer;
		transition: background-color 0.3s ease;

		&--valid {
			background-color: $letbane-color-identityred;

			&:hover {
				background-color: $letbane-color-identityred-hover;
			}

			&:focus {
				background-color: $letbane-color-identityred-pressed;
			}

			&:disabled {
				background-color: $letbane-color-identityred-disabled;
			}
		}

		img {
			margin-right: 0.625rem;
		}
	}

	&__payment-options {
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100%;
		height: 1.625rem;
		margin-bottom: 0.5625rem;
		cursor: pointer;
	}

	&__phone-input,
	&__email-input {
		width: 100%;
		height: 2.75rem;
		box-sizing: border-box;
		padding: 0 0.875rem;
		font-size: 0.875rem;
		border: 0.0625rem solid $letbane-color-stroke;
		border-radius: 0.25rem;
		margin-bottom: 0.6875rem;
		box-sizing: border-box;

		&--error {
			border: 0.0625rem solid red;
		}
	}

	&__checkboxes {
		margin-bottom: 0.625rem;
	}
}

.toggle {
	&__label {
		font-size: 0.875rem;
		font-weight: 400;
		transition: color 0.3s ease;

		&:first-of-type {
			justify-self: flex-end;
		}

		&:last-of-type {
			justify-self: flex-start;
		}

		&--checked {
			color: $letbane-color-identityred;
		}
	}

	&__input {
		position: relative;
		width: 3.875rem;
		height: 2rem;

		&--switch {
			opacity: 0;

			&:checked + .toggle__input--slider:before {
				transform: translateX(1.75rem);
			}
		}

		&--slider {
			position: absolute;
			cursor: pointer;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background-color: $letbane-color-white;
			border: 0.0625rem solid $letbane-color-stroke;
			transition: 0.4s;
			border-radius: 2.125rem;
			box-sizing: border-box;

			&:before {
				position: absolute;
				content: '';
				height: 1.75rem;
				width: 1.75rem;
				left: 0.125rem;
				bottom: 0.0625rem;
				background-color: $letbane-color-identityred;
				border: 0.125rem solid #e5573b;
				-webkit-transition: 0.4s;
				transition: 0.4s;
				border-radius: 50%;
				box-sizing: border-box;
			}
		}
	}
}

.ticket {
	display: grid;
	align-items: center;
	grid-template-columns: 2fr 1fr 5.625rem;
	padding: 0.6875rem 1.0625rem 0.6875rem 0.875rem;

	&:not(:last-child) {
		border-bottom: 0.0625rem solid $letbane-color-stroke;
	}

	&__text {
		display: flex;
		align-items: center;
		column-gap: 0.625rem;
		position: relative;
	}

	&__name {
		font-size: 0.875rem;
		text-transform: uppercase;
	}

	&__info-button {
		width: 1.0625rem;
		height: 1.0625rem;
		background-color: $letbane-color-stroke;
		border-radius: 50%;
		display: flex;
		justify-content: center;
		align-items: center;
		flex-shrink: 0;
		cursor: pointer;

		span {
			font-size: 0.5625rem;
		}
	}

	&__price {
		justify-self: center;
		font-size: 0.875rem;
	}

	&__amount-container {
		display: flex;
		justify-self: flex-end;
		align-items: center;
		column-gap: 0.5rem;
	}

	&__increase,
	&__decrease {
		position: relative;
		background-color: $letbane-color-black;
		width: 1.5rem;
		height: 1.5rem;
		border-radius: 0.125rem;
		flex-shrink: 0;
		cursor: pointer;

		&:focus {
			outline: none;
		}
	}

	&__line {
		&--horizontal {
			position: absolute;
			top: 0.6875rem;
			left: 0.4375rem;
			width: 0.625rem;
			height: 0.125rem;
			background-color: $letbane-color-white;
		}

		&--vertical {
			position: absolute;
			top: 0.4375rem;
			left: 0.6875rem;
			width: 0.125rem;
			height: 0.625rem;
			background-color: white;
		}
	}

	&__current-amount {
		font-size: 1.125rem;
	}
}

.warning {
	font-size: 0.75rem;
	font-weight: 300;
	line-height: 0.945rem;
	padding: 0.6875rem 1.0625rem 0.6875rem 0.875rem;
}

.options {
	&__text {
		font-size: 0.875rem;
		font-weight: 300;
		display: block;
		transform: translateY(-0.125rem);
	}

	&__underline {
		height: 0.0313rem;
		width: 100%;
		background-color: $letbane-color-black;
		transform: translateY(0.1875rem);
	}
}

.logos {
	display: flex;
	justify-content: center;

	&__cards {
		display: flex;
		align-items: center;
		column-gap: 0.5625rem;
	}

	&__card {
		max-height: 2.1875rem;

		&--apple {
			height: 1.4375rem;
		}

		&--paypal {
			height: 1.4375rem;
		}
	}

	&__mobilepay {
		max-height: 2.1875rem;
	}
}

.checkbox {
	display: flex;
	align-items: center;
	cursor: pointer;

	&:not(:last-child) {
		margin-bottom: 0.625rem;
	}

	&__input {
		width: 1.5rem;
		height: 1.5rem;
		margin: 0 0.625rem 0 0;
		-moz-appearance: none;
		-webkit-appearance: none;
		-o-appearance: none;
		border: 0.0625rem solid $letbane-color-stroke;
		border-radius: 0.125rem;
		background-color: $letbane-color-white;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;

		&:checked:before {
			content: '';
			width: 1rem;
			height: 1rem;
			background-color: $letbane-color-identityred;
			border-radius: 0.125rem;
		}

		&--error {
			border: 0.0625rem solid red;
		}
	}

	&__label {
		font-size: 0.875rem;
		font-weight: 300;
		cursor: pointer;
	}

	&__button {
		font-size: 0.875rem;
		font-weight: 300;
		margin-bottom: -0.3rem;
		cursor: pointer;
	}

	&__underline {
		height: 0.0313rem;
		width: 100%;
		background-color: $letbane-color-black;
		margin-top: 0.25rem;
	}
}
</style>
